import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';

export type CashFlowDirection = 'INCOME' | 'EXPENSE';
export type CashFlowCategory = 'CUSTOMER_PAYMENT' | 'EXPENSE' | 'COMMISSION' | 'REFUND';
export type CashFlowPaymentMethod = 'CASH' | 'BANK_TRANSFER' | 'CREDIT_CARD';

export interface CashFlowItem {
  id: string;
  transactionNo: string;
  transactionDate: string;
  bookingId: string;
  bookingNo: string;
  customerName: string;
  direction: CashFlowDirection;
  category: CashFlowCategory;
  description: string;
  paymentMethod: CashFlowPaymentMethod;
  amount: number;
  creditCardFeePercent: number;
  creditCardFeeAmount: number;
  creditCardFeeResponsibility: 'CUSTOMER' | 'COMPANY' | null;
  requestedByName: string;
  approvedByName: string;
  documentUrl: string | null;
}

export interface CashFlowFilters {
  dateFrom: string;
  dateTo: string;
  direction: 'ALL' | CashFlowDirection;
  category: 'ALL' | CashFlowCategory;
  paymentMethod: 'ALL' | CashFlowPaymentMethod;
}

@Injectable({ providedIn: 'root' })
export class CashFlowService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.apiUrl}/accounting/cash-flow`;

  async getItems(filters: CashFlowFilters): Promise<CashFlowItem[]> {
    let params = new HttpParams()
      .set('direction', filters.direction)
      .set('category', filters.category)
      .set('paymentMethod', filters.paymentMethod);
    if (filters.dateFrom) params = params.set('dateFrom', filters.dateFrom);
    if (filters.dateTo) params = params.set('dateTo', filters.dateTo);

    const response = await firstValueFrom(
      this.http.get<{ items: CashFlowItem[] }>(this.apiUrl, {
        params,
        withCredentials: true,
      }),
    );
    return response.items;
  }

  resolveFileUrl(fileUrl: string): string {
    if (/^https?:\/\//i.test(fileUrl)) return fileUrl;
    if (/^https?:\/\//i.test(environment.apiUrl)) {
      return `${new URL(environment.apiUrl).origin}${fileUrl}`;
    }
    return fileUrl;
  }
}
